import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import "./index.scss";

import { apiPost } from "../../../utils/apiUtils/api";
import CustomAlert from "../Alert";
import Logo from "../../../Assests/Images/Logo.svg";
import ErrorMessage from "../ErrorMessage";
import Password from "../../../SharableInputs/Password";
import PasswordRequirements from "./PasswordRequirements";
import { USER_UPDATE_PASSWORD } from "../../../utils/apiUtils/apiUrls";
import superAdminLogoMobile from "../../../Assests/Images/login-banner-mobile.svg";
import mobileLogo from "../../../Assests/Images/mobile-logo.svg";
import superAdminLogo from "../../../Assests/Images/superAdminlogin.svg";
import { setResetPasswordStatus } from "../../../Store/reducers/AppReducer";
import {
  passwordMatch,
  passwordRegex,
  passwordrequired,
  passwordrequirements,
} from "../../../utils/constants";
import Button from "../../../SharableInputs/Button";
import { clearApiError } from "../../../Store/reducers/APIReducer";

const PasswordForm = ({ formHeader, isGotoLogin }) => {
  const navigate = useNavigate();
  const disptach = useDispatch();
  const tokenParam = useParams();
  const { resetPasswordStatus } = useSelector((state) => state.App);
  const { loading, error } = useSelector((state) => state.api);

  const [email, setEmail] = useState("");
  const [isDiffrentUserName, setIsDiffrentUserName] = useState(false);
  const [ispasswordInvalid, setIspasswordInvalid] = useState(false);

  const handleResponse = (type, message) => {
    disptach(
      setResetPasswordStatus({
        isOpen: true,
        type: type,
        message: message,
      })
    );
  };

  useEffect(() => {
    if (tokenParam) {
      setEmail(tokenParam?.token);
      disptach(clearApiError());
    }
  }, []);

  useEffect(() => {
    if (error) {
      handleResponse("error", error);
    }
  }, [error]);

  const resetSchema = Yup.object().shape({
    password: Yup.string()
      .matches(passwordRegex, "regex")
      .test("different-from-email", "diffrentEmail", (value) => {
        if (value === email) {
          setIsDiffrentUserName(false);
        }
        return value !== email;
      })
      .required(passwordrequired),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("password"), null], passwordMatch)
      .required("Confirm Password is required"),
  });

  const formik = useFormik({
    initialValues: {
      confirmPassword: "",
      password: "",
    },
    validateOnBlur: true,
    validateOnChange: true,
    validationSchema: resetSchema,
    onSubmit: async () => {
      if (Object.keys(formik.errors).length === 0) {
        try {
          const response = await apiPost(USER_UPDATE_PASSWORD, {
            email: email,
            password: formik.values.password,
          });
          if (response?.isSuccess) {
            handleResponse(
              "success",
              isGotoLogin ? response?.result : "Password set successfully"
            );
            navigate("/");
          }
        } catch (error) {
        }
      }
    },
  });

  useEffect(() => {
    if (formik.errors.password === "regex") {
      setIspasswordInvalid(true);
      setIsDiffrentUserName(false);
    } else if (formik.errors.password === "diffrentEmail") {
      setIsDiffrentUserName(true);
      setIspasswordInvalid(false);
    } else {
      setIspasswordInvalid(false);
      setIsDiffrentUserName(false);
    }
  }, [formik]);

  return (
    <div className="login-wrapper">
      <div className="sm-row login-container d-lg-flex d-md-flex justify-content-between position-absolute fixed-bottom h-100">
        <div className="col-lg-6 col-md-6 col-sm-12 left-container text-center overflow-hidden align-content-end">
          <img
            className="login-img desktop-img"
            src={superAdminLogo}
            alt="super-admin-login"
          />
          <img
            className="login-img mobile-img"
            src={superAdminLogoMobile}
            alt="super-admin-login"
          />
          <div className="logo-continer col-11">
            <img className="mobile-logo" src={mobileLogo} alt="logo" />
          </div>
        </div>
        <div className="col-lg-6 col-md-6 col-sm-12 right-container d-flex">
          <div className="form-container d-flex flex-column justify-content-around p-3 col-10">
            <div className="logo-continer">
              <img src={Logo} alt="logo" />
            </div>
            <div className="form-feilds">
              <h1 className="form-header fw-bold mb-5 mb-md-3 mb-lg-3 lh-1">{formHeader}</h1>
              <form
                onSubmit={formik.handleSubmit}
                className="d-flex flex-column gap-4 gap-md-3 gap-lg-3"
              >
                <div>
                  <Password
                    placeholder="Enter password"
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    label="Password"
                    name="password"
                    onBlur={formik.handleBlur}
                    isError={formik.touched.password && formik.errors.password}
                  />
                  {formik.touched.password && formik.errors.password && (
                    <ErrorMessage message={passwordrequirements} />
                  )}
                </div>
                <div>
                  <Password
                    placeholder="Enter password"
                    value={formik.values.confirmPassword}
                    onChange={formik.handleChange}
                    label="Confirm Password"
                    name="confirmPassword"
                    onBlur={formik.handleBlur}
                    isError={
                      formik.touched.confirmPassword &&
                      formik.errors.confirmPassword
                    }
                  />
                  {formik.touched.confirmPassword &&
                    formik.errors.confirmPassword && (
                      <ErrorMessage message={formik.errors.confirmPassword} />
                    )}
                </div>
                <PasswordRequirements
                  formik={formik}
                  isDiffrentUserName={isDiffrentUserName}
                  ispasswordInvalid={ispasswordInvalid}
                />
                <Button
                  type="submit"
                  label="Submit"
                  primary={true}
                  isLoading={loading}
                  onClick={formik.handleSubmit}
                  className="align-self-end"
                />
                {isGotoLogin && (
                  <div className="goto-login">
                    <Button
                      label="Go to login"
                      onClick={() => navigate("/")}
                      secondary={true}
                    />
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
      <CustomAlert
        isOpen={resetPasswordStatus?.isOpen}
        type={resetPasswordStatus?.type}
        message={resetPasswordStatus?.message}
        onClose={() =>
          disptach(
            setResetPasswordStatus({
              isOpen: false,
              type: "",
              message: "",
            })
          )
        }
      />
    </div>
  );
};

export default PasswordForm;
